import { Anchor, Flex, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { type FunctionComponent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';

import { messages } from './messages';
import { VARIANT_CODE_REVIEW } from '../../../../../constants';
import RovoDevIconSVG from './icon.svg';
import { type JSX } from 'react/jsx-runtime';

const DISCOVER_ROVO_DEV_URL = '/rovodev/overview?details=review';

const styles = cssMap({
	headerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBlockEnd: token('space.100'),
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		alignItems: 'flex-start',
	},
	link: {
		textDecoration: 'none',
	},
});

const VariantIcon: FunctionComponent = () => <Image src={RovoDevIconSVG} alt="" width={40} />;

interface PromoCardProps {
	onDismissed?: () => void;
	tenantId: string;
	accountId: string;
}

/**
 * "..." opens a menu; Dismiss is the "Not interested" item inside that menu.
 * Same pattern as JiraSideNavFirstPartyRecommendation → JiraSideNavMessage → AdControls.
 */
const PromoCard: FunctionComponent<PromoCardProps> = ({ onDismissed, tenantId, accountId }) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const handleDismissedFromMenu = useCallback(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'post-office.jira-side-nav-rovo-dev-adoption-ipm.dismiss',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CODE_REVIEW,
			},
		);
		onDismissed?.();
	}, [fireAnalyticsEvent, tenantId, accountId, onDismissed]);

	const handleDiscoverClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'discoverRovodev',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: DISCOVER_ROVO_DEV_URL,
			},
			additionalMetadataFromFrontend: {
				variant: VARIANT_CODE_REVIEW,
			},
		});

		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'post-office.jira-side-nav-rovo-dev-adoption-ipm.discover-rovodev',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CODE_REVIEW,
			},
		);
	}, [messageInstanceClicked, fireAnalyticsEvent, tenantId, accountId]);

	useEffect(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'ipm',
				actionSubjectId: 'jira-side-nav-rovo-dev-adoption-ipm',
			},
			{
				tenantId,
				accountId,
				variant: VARIANT_CODE_REVIEW,
			},
		);
	}, [fireAnalyticsEvent, tenantId, accountId]);

	const bodyContent = (
		<Flex xcss={cx(styles.headerContainer)}>
			<Stack xcss={cx(styles.contentContainer)}>
				<VariantIcon />
				<Heading size="xsmall">{formatMessage(messages.heading)}</Heading>
				<Anchor
					href={DISCOVER_ROVO_DEV_URL}
					target="_blank"
					xcss={styles.link}
					onClick={handleDiscoverClick}
				>
					{formatMessage(messages.discoverButton)}
				</Anchor>
			</Stack>
		</Flex>
	);

	return (
		<JiraSideNavMessage
			onDismissed={handleDismissedFromMenu}
			bodyContent={bodyContent}
			showWhyAmISeeingThis={false}
		/>
	);
};

type VariantRenderProps = {
	onDismissed?: () => void;
	tenantId: string;
	accountId: string;
};

export const render = ({ onDismissed, tenantId, accountId }: VariantRenderProps): JSX.Element => (
	<PromoCard onDismissed={onDismissed} tenantId={tenantId} accountId={accountId} />
);

export default render;
